// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface PageHeaderContextType {
  breadcrumbTitle: string | null;
  setBreadcrumbTitle: (title: string | null) => void;
  breadcrumbItems: BreadcrumbItem[] | null;
  setBreadcrumbItems: (items: BreadcrumbItem[] | null) => void;
  backTo: string | null;
  setBackTo: (to: string | null) => void;
  hideBreadcrumbBack: boolean;
  setHideBreadcrumbBack: (hide: boolean) => void;
}

const PageHeaderContext = createContext<PageHeaderContextType>({
  breadcrumbTitle: null,
  setBreadcrumbTitle: () => {},
  breadcrumbItems: null,
  setBreadcrumbItems: () => {},
  backTo: null,
  setBackTo: () => {},
  hideBreadcrumbBack: false,
  setHideBreadcrumbBack: () => {},
});

export function PageHeaderProvider({ children }: Readonly<{ children: ReactNode }>) {
  // Each useState exposes its setter directly to consumers — no need for
  // the previous useCallback indirection (S6754).
  const [breadcrumbTitle, setBreadcrumbTitle] = useState<string | null>(null);
  const [breadcrumbItems, setBreadcrumbItems] = useState<BreadcrumbItem[] | null>(null);
  const [backTo, setBackTo] = useState<string | null>(null);
  const [hideBreadcrumbBack, setHideBreadcrumbBack] = useState<boolean>(false);

  // Memoise the provider value so consumers don't re-render every parent
  // render (S6481). React's setState identity is stable, so the only
  // changing inputs are the four state values themselves.
  const value = useMemo<PageHeaderContextType>(
    () => ({
      breadcrumbTitle,
      setBreadcrumbTitle,
      breadcrumbItems,
      setBreadcrumbItems,
      backTo,
      setBackTo,
      hideBreadcrumbBack,
      setHideBreadcrumbBack,
    }),
    [breadcrumbTitle, breadcrumbItems, backTo, hideBreadcrumbBack],
  );

  return (
    <PageHeaderContext.Provider value={value}>
      {children}
    </PageHeaderContext.Provider>
  );
}

export function usePageHeader() {
  return useContext(PageHeaderContext);
}
